import type { RootState } from '@src/core/store';
import { ProductBookingSlot } from '../types/productBookingSlot';
import { getFirstIncompleteEmbedSlot } from './getFirstIncompleteEmbedSlot';
import { getOrderedEmbedSlots } from './getOrderedEmbedSlots';
import { SequentialEmbedBlock } from '../types/sequentialEmbedBlock';

const embedSlotIndex = (slots: ProductBookingSlot[], slot: ProductBookingSlot): number => {
  return slots.indexOf(slot);
};

/**
 * When opening `clickedSlot`, returns block info if a prior slot is still incomplete.
 *
 * Allocation handling is split by clicked-slot position:
 *  - For slots BEFORE `seat_map` (e.g. `tariff`, `time`): we ignore
 *    `seatMapAllocationStatus.success`. The allocation outcome depends on the
 *    (tariffs + time) combination, and the inline alert tells the user to "add more
 *    tariffs OR try another hour" — both fixes happen in slots upstream of seat_map,
 *    so they must remain openable.
 *  - For `seat_map` and slots AFTER it: we keep the strict check. Once allocation has
 *    failed, opening the seat map is pointless (there are no allocatable seats), so
 *    these slots stay blocked until the user fixes the upstream selection.
 *
 * The add-to-cart guard (`useGuardedAddToCartClick`) does not opt out either, so
 * checkout stays blocked until allocation succeeds.
 */
export const getSequentialOpenBlock = (
  widgetId: string,
  clickedSlot: ProductBookingSlot,
  state: RootState,
): SequentialEmbedBlock | null => {
  const slots = getOrderedEmbedSlots(widgetId, state);
  const iClicked = embedSlotIndex(slots, clickedSlot);
  const iSeatMap = embedSlotIndex(slots, 'seat_map');
  const isClickedBeforeSeatMap = iSeatMap < 0 || (iClicked >= 0 && iClicked < iSeatMap);

  const first = getFirstIncompleteEmbedSlot(widgetId, state, {
    skipSeatMapAllocationCheck: isClickedBeforeSeatMap,
  });
  if (!first) {
    return null;
  }

  const iBlocked = embedSlotIndex(slots, first.blockingSlot);

  if (iClicked < 0 || iBlocked < 0 || iBlocked >= iClicked) {
    return null;
  }

  return first;
};
